/**
 * lazyWithRetry
 * Drop-in replacement for React.lazy for code-split route imports.
 *
 * Route chunks carry content-hashed filenames. After a deploy, a returning user
 * holding a stale index.html can request an old-hash chunk that no longer exists
 * (ChunkLoadError). This wrapper:
 *   1. retries the failed dynamic import once (covers transient network blips), then
 *   2. on a second failure, captures the error to Sentry and performs a single
 *      cache-busting reload to fetch a fresh index.html (with current chunk URLs).
 *
 * The reload is guarded by a sessionStorage flag so a genuinely-missing chunk can
 * only trigger one reload per tab — after that the error rethrows and the app's
 * ErrorBoundaryPage handles it normally. The flag is cleared on any successful
 * import so a later deploy can recover again.
 *
 * A plain window.location.reload() honors the HTTP cache and could re-serve the
 * same stale index.html, so recovery navigates to the current URL with a throwaway
 * `cb` query param to force a fresh document fetch.
 */
import React from "react";
import * as Sentry from "@sentry/react";

export const RELOAD_FLAG = "chunk-reload-attempted";
export const RECOVERY_PARAM = "cb";

const getReloadFlag = () => {
  try {
    return !!window.sessionStorage.getItem(RELOAD_FLAG);
  } catch (_e) {
    return false;
  }
};

const setReloadFlag = () => {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, "1");
  } catch (_e) {
    // sessionStorage unavailable (private mode / disabled) — degrade gracefully.
  }
};

const clearReloadFlag = () => {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG);
  } catch (_e) {
    // ignore
  }
};

// Storage-independent reload guard. The cache-busting param we add to the URL
// before reloading also marks "this tab already recovered once". This bounds
// recovery to a single reload even when sessionStorage throws (private mode /
// quota), where the flag above silently no-ops — without it, a genuinely
// missing chunk would reload forever (fresh cb each time bypasses the cache).
const urlHasRecoveryParam = () => {
  try {
    return new URL(window.location.href).searchParams.has(RECOVERY_PARAM);
  } catch (_e) {
    return false;
  }
};

// Clear both guards on a successful import: drop the sessionStorage flag and
// strip the cache-busting param from the URL so it doesn't linger across in-app
// navigation and so a later deploy can recover again. replaceState avoids
// adding a history entry.
const clearRecoveryState = () => {
  clearReloadFlag();
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(RECOVERY_PARAM)) {
      url.searchParams.delete(RECOVERY_PARAM);
      window.history.replaceState(window.history.state, "", url.toString());
    }
  } catch (_e) {
    // ignore
  }
};

const cacheBustingReload = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(RECOVERY_PARAM, Date.now().toString()); // distinct URL ⇒ bypass HTTP cache
    window.location.replace(url.toString());
  } catch (_e) {
    // Fallback to a plain reload if URL/replace is unavailable for any reason.
    try {
      window.location.reload();
    } catch (_e2) {
      // nothing more we can do
    }
  }
};

/**
 * The retry/reload loader. Exported separately so it can be unit-tested without
 * rendering a React tree.
 * @param {() => Promise<any>} importFn - a dynamic import() factory
 * @returns {Promise<any>} the imported module (or a never-resolving promise while a recovery reload is in flight)
 */
export const retryImport = async (importFn) => {
  try {
    const mod = await importFn();
    clearRecoveryState();
    return mod;
  } catch (_err) {
    try {
      // Retry once — covers a transient network failure fetching the chunk.
      const mod = await importFn();
      clearRecoveryState();
      return mod;
    } catch (retryErr) {
      if (!getReloadFlag() && !urlHasRecoveryParam()) {
        // First failure this tab: keep the error visible (a persistent non-chunk
        // import failure also lands here), then recover via a fresh document.
        try {
          Sentry.captureException(retryErr, { tags: { source: "lazyWithRetry" } });
        } catch (_sentryErr) {
          // Sentry must never crash the loader.
        }
        setReloadFlag();
        cacheBustingReload();
        // Stay suspended (Suspense fallback) until the navigation happens.
        return new Promise(() => {});
      }
      // Already reloaded once and it still fails — let ErrorBoundaryPage handle it.
      throw retryErr;
    }
  }
};

/**
 * Wrap a dynamic import factory with retry-once + guarded cache-busting reload.
 * @param {() => Promise<any>} importFn - e.g. () => import("./views/Foo/Foo")
 * @returns {React.LazyExoticComponent<any>}
 */
export default function lazyWithRetry(importFn) {
  return React.lazy(() => retryImport(importFn));
}
